import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { GetStorageSummaryCall } from "../../redux/api/report.api";

const BLUE = "#0061FE";
const GREEN = "#22C55E";
const TEXT = "#0F172A";
const MUTED = "#64748B";

function bytesToGbNumber(bytes = 0) {
  return Number(bytes || 0) / (1024 * 1024 * 1024);
}

function formatGb(gb) {
  if (!Number.isFinite(gb)) return "0";
  if (gb >= 10) return gb.toFixed(1);
  if (gb >= 1) return gb.toFixed(1);
  return gb.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function formatLicenseEnd(licenseEnd) {
  if (!licenseEnd) return null;
  const d = new Date(licenseEnd);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getLicenseProgress(licenseStart, licenseEnd) {
  const end = new Date(licenseEnd || "");
  if (Number.isNaN(end.getTime())) return { percent: 0, color: GREEN, daysLeft: null };
  const start = new Date(licenseStart || "");
  const now = new Date();
  const msDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msDay);

  let remainingPercent = 0;
  if (!Number.isNaN(start.getTime()) && end.getTime() > start.getTime()) {
    const totalMs = end.getTime() - start.getTime();
    const remainingMs = Math.max(0, end.getTime() - now.getTime());
    remainingPercent = (remainingMs / totalMs) * 100;
  } else {
    // Fallback: treat 365-day window ending at license end
    remainingPercent = Math.min(100, Math.max(0, (Math.max(0, daysLeft) / 365) * 100));
  }

  const percent = Math.min(100, Math.max(0, remainingPercent));
  const color = daysLeft <= 14 ? "#EF4444" : daysLeft <= 45 ? "#F59E0B" : GREEN;
  return { percent, color, daysLeft };
}

function canShowForRole(role) {
  const roleKey = String(role || "").toLowerCase();
  return roleKey === "prof" || roleKey === "professor" || roleKey === "university_admin";
}

function IconBadge({ children, bg, color }) {
  return (
    <MDBox
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2.2,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: bg,
        color,
      }}
    >
      {children}
    </MDBox>
  );
}

IconBadge.propTypes = {
  children: PropTypes.node,
  bg: PropTypes.string,
  color: PropTypes.string,
};

/**
 * Sticky top strip with Storage + License status for professors / uni admins.
 */
export default function StorageLicenseBar({ sticky = true }) {
  const { t } = useTranslation();
  const role = useSelector((state) => state.auth?.role);
  const visible = canShowForRole(role);
  const [storage, setStorage] = useState({
    usedBytes: 0,
    limitBytes: 0,
    percent: 0,
    licenseStart: null,
    licenseEnd: null,
  });

  useEffect(() => {
    if (!visible) return undefined;
    let mounted = true;
    GetStorageSummaryCall().then((res) => {
      if (!mounted) return;
      setStorage({
        usedBytes: Number(res?.usedBytes || 0),
        limitBytes: Number(res?.limitBytes || 0),
        percent: Number(res?.percent || 0),
        licenseStart: res?.licenseStart || null,
        licenseEnd: res?.licenseEnd || null,
      });
    });
    return () => {
      mounted = false;
    };
  }, [visible]);

  const licenseProgress = useMemo(
    () => getLicenseProgress(storage.licenseStart, storage.licenseEnd),
    [storage.licenseStart, storage.licenseEnd]
  );
  const licenseEndLabel = formatLicenseEnd(storage.licenseEnd);

  const usedGb = bytesToGbNumber(storage.usedBytes);
  const limitGb = bytesToGbNumber(storage.limitBytes);
  const freeGb = Math.max(0, limitGb - usedGb);
  const usedPercent = Math.min(100, Math.max(0, storage.percent || (limitGb > 0 ? (usedGb / limitGb) * 100 : 0)));

  if (!visible) return null;

  return (
    <MDBox
      sx={{
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 1100,
        flexShrink: 0,
        // Keep left/right edges aligned with DashboardLayout gutter (no negative margins).
        // mb: 3 matches DashboardLayout horizontal padding (px: 3) so gap below = side gutters.
        width: "100%",
        mx: 0,
        mb: 3,
        px: 0,
        pt: 0,
        pb: 0,
        bgcolor: "transparent",
      }}
    >
      <MDBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: { xs: 1.25, md: 0 },
          bgcolor: "#FFFFFF",
          borderRadius: 3,
          border: "1px solid rgba(148, 163, 184, 0.2)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
          overflow: "hidden",
        }}
      >
        {/* Storage */}
        <MDBox
          sx={{
            flex: "1 1 280px",
            minWidth: 240,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
          }}
        >
          <IconBadge bg="linear-gradient(180deg, #E8F1FF 0%, #F5F9FF 100%)" color={BLUE}>
            <CloudOutlinedIcon sx={{ fontSize: 22 }} />
          </IconBadge>
          <MDBox sx={{ flex: 1, minWidth: 0 }}>
            <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mb={0.35}>
              <MDTypography sx={{ fontSize: 14, fontWeight: 800, color: TEXT, lineHeight: 1.2 }}>
                {t("nav.storage")}
              </MDTypography>
              <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, whiteSpace: "nowrap" }}>
                {formatGb(freeGb)} GB {t("nav.free", { defaultValue: "free" })}
              </MDTypography>
            </MDBox>
            <MDTypography sx={{ fontSize: 12, color: MUTED, mb: 0.85, lineHeight: 1.2 }}>
              {formatGb(usedGb)} GB {t("nav.of", { defaultValue: "of" })} {formatGb(limitGb)} GB{" "}
              {t("nav.used", { defaultValue: "used" })}
            </MDTypography>
            <LinearProgress
              variant="determinate"
              value={usedPercent}
              sx={{
                height: 7,
                borderRadius: 999,
                bgcolor: "#E8F1FF",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor: usedPercent >= 90 ? "#EF4444" : BLUE,
                },
              }}
            />
          </MDBox>
        </MDBox>

        {licenseEndLabel ? (
          <>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", md: "block" },
                borderColor: "rgba(148, 163, 184, 0.28)",
                my: 1.5,
              }}
            />
            {/* License */}
            <MDBox
              sx={{
                flex: "1 1 280px",
                minWidth: 240,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
              }}
            >
              <IconBadge bg="linear-gradient(180deg, #E9F9EF 0%, #F4FDF7 100%)" color={GREEN}>
                <WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />
              </IconBadge>
              <MDBox sx={{ flex: 1, minWidth: 0 }}>
                <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mb={0.35}>
                  <MDTypography sx={{ fontSize: 14, fontWeight: 800, color: TEXT, lineHeight: 1.2 }}>
                    {t("nav.license", { defaultValue: "License" })}
                  </MDTypography>
                  <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, whiteSpace: "nowrap" }}>
                    {licenseEndLabel}
                  </MDTypography>
                </MDBox>
                <MDTypography sx={{ fontSize: 12, color: MUTED, mb: 0.85, lineHeight: 1.2 }}>
                  {licenseProgress.daysLeft != null
                    ? t("nav.daysLeft", { count: Math.max(0, licenseProgress.daysLeft) })
                    : t("nav.licenseProgress")}
                </MDTypography>
                <LinearProgress
                  variant="determinate"
                  value={licenseProgress.percent}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: "#E8F0E9",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: licenseProgress.color,
                    },
                  }}
                />
              </MDBox>
            </MDBox>
          </>
        ) : null}
      </MDBox>
    </MDBox>
  );
}

StorageLicenseBar.propTypes = {
  sticky: PropTypes.bool,
};
